import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton,
  DrawerContent, DrawerHeader, DrawerOverlay, Flex, HStack, Icon,
  IconButton, Progress, Text, Tooltip, VStack, useColorModeValue, useDisclosure, useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon, CheckCircleIcon, TimeIcon } from "@chakra-ui/icons";
import { ChevronRight, Edit2, Eye, EyeOff, Hash, History, Laptop, RotateCcw, Save, Server, Shield, Tag, X } from "lucide-react";
import type { Candidate, CandidateDetail, MissionItem, Submission } from "../../../types/onboarding.ts";
import { OnflyLoading } from "../../../tools/OnflyLoading";
import { onboardingService } from "../../../services/onboardingService.ts";
import { UserAvatar } from "../../../components/UserAvatar";
import { useDownloadManager } from "../../../contexts/DownloadManagerContext";
import { useApiRequest } from "../../../hooks/useApiRequest.ts";
import { settings } from "../../../shared/settings.ts";
import { getToken, getUser } from "../../../lib/auth.ts";
import {
  getWhitelistEntry, patchWhitelistEntry, sendExamConvocation, sendBenefitsEmail, sendBenefitsPendencies, setDpFinished, uploadAso,
  type BenefitsPendencyPayload,
  type BenefitsEmailPreferences, type WhitelistEntryPatch,
} from "../../../lib/api.ts";
import type { WhitelistEntry } from "../../../types/whitelist.ts";
import { LeaderSearchInput } from "../../../components/LeaderSearchInput";
import { ChildrenInteractionBlocker } from "../../../components/overlays/ChildrenInteractionBlocker";
import { PERMISSIONS } from "../../../config/permissions";

import MissionSubmission, { EditFormSubmission } from "./components/MissionSubmission";
import { MissionListItem } from "./components/MissionListItem";
import { WaitingRhBanner } from "./components/WaitingRhBanner";
import { CandidateActivityLogSection } from "./components/CandidateActivityLogSection";
import { ADMISSION_FIELDS, type AdmissionField, type AdmissionDrafts } from "./components/AdmissionDataPanel";
import { RecruiterSection } from "./components/RecruiterSection";
import { RhProcessWorkflow } from "./components/RhProcessWorkflow";
import { getHrProgress } from "./hrProcessSteps";
import { usePermissionsContext } from "../../../data/permissions/permissionContext";
import { ReminderBadge } from "./components/ReminderBadge";
import { ConfirmCancelTicketDialog } from "./components/ConfirmCancelTicketDialog";
import { ConfirmBenefitsEmailDialog } from "./components/ConfirmBenefitsEmailDialog";
import { PendencyModal } from "./components/PendencyModal";

interface DrawerProps {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  onOptimisticPatch?: (email: string, patch: Partial<WhitelistEntry>) => void;
  mode?: "geral" | "rh";
}

const STATUS_CONFIG: Record<string, { label: string; colorScheme: string }> = {
  CONCLUIDO:    { label: "Concluído",    colorScheme: "green"  },
  EM_ANDAMENTO: { label: "Em andamento", colorScheme: "blue"   },
  AGUARDANDO:   { label: "Não Iniciado", colorScheme: "gray"   },
  AGUARDANDO_RH:{ label: "Aguardando RH",colorScheme: "orange" },
};

const progressColorScheme = (pct: number): string => {
  if (pct >= 100) return "green";
  if (pct > 0)    return "blue";
  return "gray";
};

const DEFAULT_WORK_START_TIME = "09:00";
const DEFAULT_WORK_END_TIME   = "18:00";

const EMPTY_ADMISSION_DRAFTS: AdmissionDrafts = {
  responsible_manager: "",
  job_title: "", job_activities: "", salary: "", meal_voucher: "", cost_center: "",
  workplace: "", work_schedule: "", work_start_time: "", work_end_time: "",
  admission_date: "",
};

function admissionDraftsFromEntry(entry: WhitelistEntry | null): AdmissionDrafts {
  if (!entry) return { ...EMPTY_ADMISSION_DRAFTS, work_start_time: DEFAULT_WORK_START_TIME, work_end_time: DEFAULT_WORK_END_TIME };
  return {
    responsible_manager: entry.responsible_manager ?? "",
    job_title: entry.job_title ?? "",
    job_activities: entry.job_activities ?? "",
    salary: entry.salary ?? "",
    meal_voucher: entry.meal_voucher ?? "",
    cost_center: entry.cost_center ?? "",
    workplace: entry.workplace ?? "",
    work_schedule: entry.work_schedule ?? "",
    work_start_time: entry.work_start_time ?? DEFAULT_WORK_START_TIME,
    work_end_time: entry.work_end_time ?? DEFAULT_WORK_END_TIME,
    admission_date: entry.admission_date ?? "",
  };
}

const _TRUTHY = new Set(["true", "sim", "yes", "s", "1"]);
const _FALSY  = new Set(["false", "não", "nao", "no", "n", "0"]);

function _resolveFlag(value: string | null | undefined): boolean | null {
  const v = (value ?? "").trim().toLowerCase();
  if (_TRUTHY.has(v)) return true;
  if (_FALSY.has(v))  return false;
  return null;
}

// Missões com campos de formulário (puras ou mistas com arquivos) têm campos editáveis.
const submissionHasFields = (
  submission: Submission | null | undefined,
): submission is Extract<Submission, { type: "form" | "mixed" }> =>
  submission?.type === "form" || submission?.type === "mixed";

function extractBenefitPreferences(missions: MissionItem[]): BenefitsEmailPreferences {
  let wants_wellhub:      boolean | null = null;
  let wants_plano_saude:  boolean | null = null;
  let wants_plano_odonto: boolean | null = null;

  for (const mission of missions) {
    const sub = mission.submission;
    if (!submissionHasFields(sub)) continue;
    for (const field of sub.fields) {
      const label = (field.label ?? "").toLowerCase();
      if (label.includes("wellhub") || label.includes("gympass"))
        wants_wellhub = _resolveFlag(field.value);
      if (label.includes("saúde") || label.includes("saude"))
        if (!label.includes("odonto") && !label.includes("peso") && !label.includes("altura"))
          wants_plano_saude = _resolveFlag(field.value);
      if (label.includes("odonto"))
        wants_plano_odonto = _resolveFlag(field.value);
    }
  }
  return { wants_wellhub, wants_plano_saude, wants_plano_odonto };
}

const CandidateDetailDrawer = ({ candidate, isOpen, onClose, onRefresh, onOptimisticPatch, mode = "geral" }: DrawerProps) => {
  const [selectedMission, setSelectedMission] = useState<MissionItem | null>(null);
  const [detail, setDetail]                   = useState<CandidateDetail | null>(null);
  const [approvingId, setApprovingId]         = useState<number | null>(null);
  const [resettingId, setResettingId]         = useState<number | null>(null);
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);
  const [showActivityLog, setShowActivityLog]  = useState(false);
  const [isCancelling, setIsCancelling]       = useState(false);
  const [ticketClosed, setTicketClosed]       = useState(false);
  const [isEditing, setIsEditing]             = useState(false);
  const [editValues, setEditValues]           = useState<Record<string, string>>({});
  const [isSaving, setIsSaving]               = useState(false);
  const [isUploadingAso, setIsUploadingAso]   = useState(false);
  const [isSendingExamConvocation, setIsSendingExamConvocation] = useState(false);
  const [whitelistEntry, setWhitelistEntry]   = useState<WhitelistEntry | null>(null);
  const [isLoadingWhitelist, setIsLoadingWhitelist] = useState(false);
  const [isSendingBenefitsEmail, setIsSendingBenefitsEmail] = useState(false);
  const [isNotifyingPendency, setIsNotifyingPendency] = useState(false);
  const [showCyberPassword, setShowCyberPassword] = useState(false);
  const [admissionDrafts, setAdmissionDrafts] = useState<AdmissionDrafts>({ ...EMPTY_ADMISSION_DRAFTS });
  const [isSavingAdmission, setIsSavingAdmission] = useState(false);
  const [isSavingRecruiter, setIsSavingRecruiter] = useState(false);
  const [isSettingDpFinished, setIsSettingDpFinished] = useState(false);

  const { isOpen: isConfirmOpen,   onOpen: openConfirm,   onClose: closeConfirm }   = useDisclosure();
  const { isOpen: isBenefitsOpen,  onOpen: openBenefits,  onClose: closeBenefits }  = useDisclosure();
  const { isOpen: isPendencyOpen, onOpen: openPendency, onClose: closePendency } = useDisclosure();
  const { permissions: userPermissions } = usePermissionsContext();
  const canViewSalary = userPermissions.includes(PERMISSIONS.RH.SALARY_VIEW);

  const { startDownload } = useDownloadManager();
  const { request, isLoading: isLoadingDetail } = useApiRequest();
  const { request: approveReq }  = useApiRequest();
  const { request: resetReq }    = useApiRequest();
  const { request: freshdeskReq }= useApiRequest();
  const { request: editReq }     = useApiRequest();
  const { request: resolveRgReq }= useApiRequest();
  const toast = useToast();

  const subtitleColor  = useColorModeValue("gray.500", "whiteAlpha.600");
  const labelColor     = useColorModeValue("gray.700", "whiteAlpha.900");
  const hintColor      = useColorModeValue("gray.400", "whiteAlpha.500");
  const progressBg     = useColorModeValue("gray.100", "whiteAlpha.200");
  const backBtnColor   = useColorModeValue("gray.500", "whiteAlpha.600");
  const cardBorderColor  = useColorModeValue("gray.100", "whiteAlpha.200");
  const notebookIconBg   = useColorModeValue("blue.50", "rgba(0,158,251,0.16)");
  const notebookIconColor = useColorModeValue("blue.600", "blue.300");
  const cyberIconBg      = useColorModeValue("purple.50", "rgba(128,90,213,0.16)");
  const cyberIconColor   = useColorModeValue("purple.600", "purple.300");
  const historyRowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");


  const fetchDetail = useCallback((silent = false) => {
    if (!candidate) return;
    request({
      url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/onboarding/candidates/${encodeURIComponent(candidate.email)}`,
      method: "GET", skipToast: true, silent,
    }).then((data: CandidateDetail) => setDetail(data)).catch(() => {});
  }, [candidate, request]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = useCallback(async (missionId: number) => {
    if (!candidate) return;
    setApprovingId(missionId);
    const rhEmail = getUser()?.email;
    try {
      await approveReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/missions/${missionId}/approve-wait`,
        method: "POST",
        headers: rhEmail ? { "X-User-Email": rhEmail } : undefined,
        skipToast: false,
        sucessMesage: "Aprovado! O candidato já pode continuar nas próximas etapas.",
      });
      if (selectedMission?.id === missionId) setSelectedMission(null);
      fetchDetail(); onRefresh?.();
      setActivityLogRefreshKey((k) => k + 1);
    } catch { } finally { setApprovingId(null); }
  }, [candidate, approveReq, selectedMission, fetchDetail, onRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = useCallback(async (missionId: number) => {
    if (!candidate) return;
    setResettingId(missionId);
    try {
      await resetReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/missions/${missionId}/reset`,
        method: "POST", skipToast: false, sucessMesage: "Missão resetada para PENDING.",
      });
      if (selectedMission?.id === missionId) setSelectedMission(null);
      fetchDetail(); onRefresh?.();
      setActivityLogRefreshKey((k) => k + 1);
    } catch { } finally { setResettingId(null); }
  }, [candidate, resetReq, selectedMission, fetchDetail, onRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelTicket = useCallback(async () => {
    if (!candidate) return;
    setTicketClosed(true);
    setIsCancelling(true);
    try {
      await freshdeskReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/freshdesk/cancel`,
        method: "POST", skipToast: false, sucessMesage: "Ticket Freshdesk cancelado.",
      });
    } catch { setTicketClosed(false); } finally { setIsCancelling(false); }
  }, [candidate, freshdeskReq]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleUploadAso = useCallback(async (file: File) => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    setIsUploadingAso(true);
    try {
      await uploadAso(token, candidate.email, file);
      const updated = await patchWhitelistEntry(token, candidate.email, { admission_exam_completed: true });
      setWhitelistEntry(updated);
      fetchDetail(true);
      onRefresh?.();
      toast({ title: "ASO enviado com sucesso.", status: "success", duration: 5000, isClosable: true });
    } catch (err: any) {
      toast({ title: "Erro ao enviar ASO", description: err?.message ?? "Não foi possível enviar o arquivo.", status: "error", duration: 8000, isClosable: true });
    } finally { setIsUploadingAso(false); }
  }, [candidate, fetchDetail, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convocação do exame: envia o e-mail ao candidato com a guia de agendamento anexada.
  const handleSendExamConvocation = useCallback(async (file: File): Promise<boolean> => {
    if (!candidate) return false;
    const token = getToken();
    if (!token) return false;
    setIsSendingExamConvocation(true);
    try {
      const updated = await sendExamConvocation(token, candidate.email, file);
      setWhitelistEntry(updated);
      fetchDetail(true);
      onRefresh?.();
      toast({ title: "Convocação enviada ao candidato!", status: "success", duration: 5000, isClosable: true });
      return true;
    } catch (err: any) {
      toast({ title: "Erro ao enviar convocação", description: err?.message ?? "Não foi possível enviar o e-mail.", status: "error", duration: 8000, isClosable: true });
      return false;
    } finally { setIsSendingExamConvocation(false); }
  }, [candidate, fetchDetail, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWhitelistEntry = useCallback(async () => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    setIsLoadingWhitelist(true);
    try {
      const entry = await getWhitelistEntry(token, candidate.email);
      setWhitelistEntry(entry);
    } catch (err: any) {
      if (err?.status === 404) {
        setWhitelistEntry({
          id: 0, email: candidate.email,
          candidate_full_name: candidate.name ?? null,
          cpf_number: null, candidate_phone: null, candidate_birthdate: null,
          address_zip_code: null, address_street: null, address_number: null,
          address_city: null, address_state: null, job_title: null,
          job_department: null, responsible_manager: null,
          recruiter: null, recruiter_name: null,
          salary: null, meal_voucher: null, cost_center: null, workplace: null,
          work_schedule: null, work_start_time: null, work_end_time: null,
          job_activities: null, admission_date: null,
          is_pcd: null, is_pj: null, is_tech: null, added_by: null,
          created_at: "", external_id: null, email_missing: false,
          welcome_email_status: null, benefits_email_sent: false,
          benefits_email_sent_at: null, pendency_email_sent_at: null,
          admission_exam_completed: false,
          exam_email_sent_at: null,
          exam_convocation_attachment_name: null,
          hr_process_status: "aguardando_rh", completed_steps: 0, total_steps: 0,
          dp_finished: false, onboarding_completed_at: null,
        });
      }
    } finally { setIsLoadingWhitelist(false); }
  }, [candidate]);

  useEffect(() => {
    setAdmissionDrafts(admissionDraftsFromEntry(whitelistEntry));
  }, [whitelistEntry]);

  const admissionDirty = useMemo(() => {
    const patch: WhitelistEntryPatch = {};
    if (!whitelistEntry) return { patch, hasChanges: false };
    for (const f of ADMISSION_FIELDS) {
      const orig = (whitelistEntry[f] ?? "") as string;
      const d = admissionDrafts[f] ?? "";
      if (d.trim() !== orig.trim()) {
        patch[f] = d.trim() === "" ? null : d.trim();
      }
    }
    return { patch, hasChanges: Object.keys(patch).length > 0 };
  }, [whitelistEntry, admissionDrafts]);

  const handleDraftChange = useCallback((field: AdmissionField, value: string) => {
    setAdmissionDrafts((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAssignRecruiter = useCallback(async (email: string | null, name: string | null) => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    const snapshot = whitelistEntry;
    const patch: WhitelistEntryPatch = { recruiter: email, recruiter_name: name };

    setWhitelistEntry((prev) => (prev ? { ...prev, recruiter: email, recruiter_name: name } : prev));
    onOptimisticPatch?.(candidate.email, { recruiter: email, recruiter_name: name });
    setIsSavingRecruiter(true);
    try {
      await patchWhitelistEntry(token, candidate.email, patch);
      toast({
        title: email ? "Recrutador atribuído." : "Recrutador removido.",
        status: "success", duration: 3000, isClosable: true,
      });
      onRefresh?.();
    } catch {
      setWhitelistEntry(snapshot);
      fetchWhitelistEntry();
      onRefresh?.();
      toast({ title: "Erro ao atribuir recrutador.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSavingRecruiter(false);
    }
  }, [candidate, whitelistEntry, onOptimisticPatch, fetchWhitelistEntry, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveAllAdmission = useCallback(async () => {
    if (!candidate || !whitelistEntry) return;
    const token = getToken();
    if (!token) return;
    const { patch, hasChanges } = admissionDirty;
    if (!hasChanges) return;
    const email = candidate.email;
    const snapshot = whitelistEntry;

    setWhitelistEntry((prev) => (prev ? { ...prev, ...patch } : prev));
    onOptimisticPatch?.(email, patch);
    setIsSavingAdmission(true);
    try {
      await patchWhitelistEntry(token, email, patch);
      const managerRaw = patch.responsible_manager;
      const manager = typeof managerRaw === "string" ? managerRaw.trim() : "";
      if (detail?.awaiting_rg && manager) {
        try {
          await resolveRgReq({
            url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(email)}/resolve-rg`,
            method: "PATCH",
            payload: { responsible_manager: manager },
            skipToast: false, sucessMesage: "Gestor informado. Admissão concluída!",
          });
        } catch {
          /* o gestor já foi salvo no gg-hub; falha do resolve-rg não reverte o patch */
        }
      }
      toast({ title: "Dados da admissão atualizados.", status: "success", duration: 3000, isClosable: true });
      onRefresh?.();
      fetchDetail();
    } catch {
      setWhitelistEntry(snapshot);
      fetchWhitelistEntry();
      onRefresh?.();
      toast({ title: "Erro ao salvar os dados da admissão.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSavingAdmission(false);
    }
  }, [candidate, whitelistEntry, admissionDirty, detail, onOptimisticPatch, fetchWhitelistEntry, fetchDetail, onRefresh, resolveRgReq, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDpFinished = useCallback(async (finished: boolean) => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    setIsSettingDpFinished(true);
    try {
      const updated = await setDpFinished(token, candidate.email, finished);
      setWhitelistEntry(updated);
      onOptimisticPatch?.(candidate.email, { dp_finished: finished });
      onRefresh?.();
      toast({ title: finished ? "DP marcado como finalizado." : "DP marcado como pendente.", status: "success", duration: 3000, isClosable: true });
    } catch (err: any) {
      toast({ title: "Erro ao atualizar status do DP.", description: err?.message, status: "error", duration: 5000, isClosable: true });
    } finally { setIsSettingDpFinished(false); }
  }, [candidate, onOptimisticPatch, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendBenefitsEmail = useCallback(async () => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    setIsSendingBenefitsEmail(true);
    try {
      const preferences = detail ? extractBenefitPreferences(detail.missions) : undefined;
      const updated = await sendBenefitsEmail(token, candidate.email, preferences);
      setWhitelistEntry(updated);
      onRefresh?.();
      toast({ title: "E-mail de benefícios enviado!", status: "success", duration: 5000, isClosable: true });
    } catch (err: any) {
      toast({ title: "Erro ao enviar e-mail", description: err?.message ?? "Não foi possível enviar o e-mail de benefícios.", status: "error", duration: 8000, isClosable: true });
    } finally { setIsSendingBenefitsEmail(false); }
  }, [candidate, detail, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotifyPendencies = useCallback(async (payload: BenefitsPendencyPayload) => {
    if (!candidate) return;
    const token = getToken();
    if (!token) return;
    setIsNotifyingPendency(true);
    try {
      const updated = await sendBenefitsPendencies(token, candidate.email, payload);
      setWhitelistEntry(updated);
      closePendency();
      onRefresh?.();
      toast({ title: "Cobrança de pendências enviada!", description: "A missão foi reaberta e o candidato receberá o e-mail.", status: "success", duration: 5000, isClosable: true });
    } catch (err: any) {
      toast({ title: "Erro ao cobrar pendências", description: err?.message ?? "Não foi possível enviar a cobrança.", status: "error", duration: 8000, isClosable: true });
    } finally { setIsNotifyingPendency(false); }
  }, [candidate, closePendency, onRefresh, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const initEditValues = useCallback(() => {
    if (!selectedMission || !submissionHasFields(selectedMission.submission)) return;
    const values: Record<string, string> = {};
    selectedMission.submission.fields.forEach(f => {
      const ft = (f.field_type ?? "").toUpperCase();
      if (ft !== "FILE" && ft !== "FILE_LIST") values[f.key] = f.value ?? "";
    });
    setEditValues(values);
    setIsEditing(true);
  }, [selectedMission]);

  const handleSaveFields = useCallback(async () => {
    if (!candidate || !selectedMission) return;
    setIsSaving(true);
    try {
      const fields = Object.entries(editValues).map(([key, value]) => ({ key, value: value === "" ? null : value }));
      await editReq({
        url: `${settings.environment.VITE_STARTER_PACK_BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(candidate.email)}/missions/${selectedMission.id}/fields`,
        method: "PATCH", payload: { fields }, skipToast: false, sucessMesage: "Campos atualizados com sucesso.",
      });
      setIsEditing(false); fetchDetail();
    } catch { } finally { setIsSaving(false); }
  }, [candidate, selectedMission, editValues, editReq, fetchDetail]);

  const handleDownloadFiles = useCallback(async () => {
    if (!candidate) return;
    try {
      await startDownload({ user_email: candidate.email }, candidate.name || candidate.email, settings.environment.VITE_STARTER_PACK_BASE_URL!);
    } catch {
      toast({ title: "Erro ao iniciar download", description: "Não foi possível criar o job de exportação.", status: "error", duration: 8000, isClosable: true });
    }
  }, [candidate, startDownload, toast]);


  useEffect(() => {
    setSelectedMission(null);
    setDetail(null);
    setTicketClosed(false);
    setWhitelistEntry(null);
    setShowActivityLog(false);
  }, [candidate]);

  useEffect(() => { if (detail) setTicketClosed(detail.ticket_closed ?? false); }, [detail]);
  useEffect(() => { setIsEditing(false); setEditValues({}); }, [selectedMission]);
  useEffect(() => {
    if (!isOpen || !candidate) return;
    fetchDetail(); fetchWhitelistEntry();
  }, [isOpen, candidate]);

  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => { html.style.overflow = prev; };
  }, [isOpen]);


  if (!candidate) return null;

  const { name, email, position, department, completed_missions, total_missions, progress_pct, status, is_pj } = candidate;
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.AGUARDANDO;
  const statusLabel = mode === "rh" && status === "AGUARDANDO_RH" ? "Não Iniciado" : config.label;
  const effectiveIsPj = whitelistEntry?.is_pj ?? is_pj ?? false;
  const hrProgress = mode === "rh" ? getHrProgress({ whitelist: whitelistEntry, canViewSalary, isPj: effectiveIsPj }) : null;
  const missions: MissionItem[] = (detail?.missions ?? []).filter(m => {
    if (is_pj === null || is_pj === undefined) return true;
    const isClt = !is_pj;
    return (is_pj && (m.is_pj ?? true)) || (isClt && (m.is_clt ?? true));
  });

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" pb={4}>
            <HStack spacing={3} pr={8} align="center">
              <UserAvatar size="md" name={name} email={email} />
              <Box flex={1} minW={0}>
                <Text fontWeight="bold" fontSize="md" lineHeight="1.2">{name}</Text>
                <HStack mt={1.5} spacing={1}>
                  {is_pj && <Badge colorScheme="yellow" variant="subtle" fontSize="xs">PJ</Badge>}
                  <Badge colorScheme={config.colorScheme} variant="subtle" fontSize="xs">{statusLabel}</Badge>
                </HStack>
              </Box>
            </HStack>

            {mode === "rh" && (
              <VStack align="stretch" spacing={3} mt={4} pr={8}>
                <RecruiterSection
                  variant="header"
                  recruiterEmail={whitelistEntry?.recruiter ?? null}
                  onAssign={handleAssignRecruiter}
                  isSaving={isSavingRecruiter}
                  token={getToken() ?? ""}
                />
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs" color={subtitleColor}>Progresso do processo</Text>
                    <Text fontSize="xs" color={subtitleColor}>{hrProgress!.completed} de {hrProgress!.total} etapas</Text>
                  </Flex>
                  <Progress value={hrProgress!.percent} size="sm" borderRadius="full" colorScheme={hrProgress!.percent >= 100 ? "green" : "blue"} />
                </Box>
              </VStack>
            )}
          </DrawerHeader>

          <DrawerBody pt={5}>
            {showActivityLog ? (
              <VStack align="stretch" spacing={5}>
                <Button
                  leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" alignSelf="flex-start"
                  onClick={() => setShowActivityLog(false)} color={backBtnColor} px={0}
                  _hover={{ bg: "transparent", color: "blue.500" }}
                >
                  Voltar para missões
                </Button>
                <HStack spacing={2}>
                  <Icon as={History} color="blue.500" boxSize={4} />
                  <Text fontWeight="bold" fontSize="md">Histórico de ações</Text>
                </HStack>
                <CandidateActivityLogSection email={candidate.email} refreshKey={activityLogRefreshKey} />
              </VStack>
            ) : selectedMission ? (
              <VStack align="stretch" spacing={5}>
                <Button
                  leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" alignSelf="flex-start"
                  onClick={() => setSelectedMission(null)} color={backBtnColor} px={0}
                  _hover={{ bg: "transparent", color: "blue.500" }}
                >
                  Voltar para missões
                </Button>

                <Box>
                  <HStack spacing={2} mb={1}>
                    <Icon
                      as={selectedMission.waiting_rh ? TimeIcon : selectedMission.completed ? CheckCircleIcon : TimeIcon}
                      color={selectedMission.waiting_rh ? "orange.400" : selectedMission.completed ? "green.500" : "gray.300"}
                      boxSize={4}
                    />
                    <Text fontWeight="bold" fontSize="md">{selectedMission.title}</Text>
                  </HStack>
                  <Flex align="center" justify="space-between">
                    <Badge
                      colorScheme={selectedMission.waiting_rh ? "orange" : selectedMission.completed ? "green" : "gray"}
                      variant="subtle" fontSize="xs"
                    >
                      {selectedMission.waiting_rh ? "Aguardando RH" : selectedMission.completed ? "Concluída" : "Pendente"}
                    </Badge>
                    {submissionHasFields(selectedMission.submission) && !isEditing && (
                      <ChildrenInteractionBlocker
                        requiredPermissions={[PERMISSIONS.RH.EDIT_MISSION_FIELDS]}
                        toastMessage="Você não tem permissão para editar campos de missões."
                      >
                        <Button size="xs" leftIcon={<Icon as={Edit2} boxSize={3} />} variant="ghost" colorScheme="blue" onClick={initEditValues}>
                          Editar campos
                        </Button>
                      </ChildrenInteractionBlocker>
                    )}
                  </Flex>
                </Box>

                <Divider />

                {isEditing && submissionHasFields(selectedMission.submission) ? (
                  <VStack align="stretch" spacing={4}>
                    <EditFormSubmission
                      fields={selectedMission.submission.fields}
                      editValues={editValues}
                      onChangeField={(key, val) => setEditValues(prev => ({ ...prev, [key]: val }))}
                    />
                    <HStack justify="flex-end" spacing={2} pt={2}>
                      <Button size="sm" variant="ghost" leftIcon={<Icon as={X} boxSize={3.5} />} onClick={() => setIsEditing(false)}>
                        Cancelar
                      </Button>
                      <ChildrenInteractionBlocker
                        requiredPermissions={[PERMISSIONS.RH.EDIT_MISSION_FIELDS]}
                        toastMessage="Você não tem permissão para editar campos de missões."
                      >
                        <Button size="sm" colorScheme="blue" leftIcon={<Icon as={Save} boxSize={3.5} />}
                          isLoading={isSaving} loadingText="Salvando..." onClick={handleSaveFields}>
                          Salvar
                        </Button>
                      </ChildrenInteractionBlocker>
                    </HStack>
                  </VStack>
                ) : (
                  <MissionSubmission submission={selectedMission.submission} />
                )}
              </VStack>
            ) : mode === "rh" ? (
              (isLoadingDetail || isLoadingWhitelist) ? (
                <Flex justify="center" py={12}><OnflyLoading containerSize={60} /></Flex>
              ) : (
                <RhProcessWorkflow
                  candidate={candidate}
                  whitelistEntry={whitelistEntry}
                  detail={detail}
                  token={getToken() ?? ""}
                  admissionDrafts={admissionDrafts}
                  onDraftChange={handleDraftChange}
                  onSaveAdmission={handleSaveAllAdmission}
                  isSavingAdmission={isSavingAdmission}
                  hasAdmissionChanges={admissionDirty.hasChanges}
                  onSendExamConvocation={handleSendExamConvocation}
                  isSendingExamConvocation={isSendingExamConvocation}
                  onUploadAso={handleUploadAso}
                  isUploadingAso={isUploadingAso}
                  onSendBenefits={openBenefits}
                  isSendingBenefitsEmail={isSendingBenefitsEmail}
                  onNotifyPendencies={openPendency}
                  pendencySentAt={whitelistEntry?.pendency_email_sent_at ?? null}
                  onSetDpFinished={handleSetDpFinished}
                  isSettingDpFinished={isSettingDpFinished}
                  canViewSalary={canViewSalary}
                  isPj={effectiveIsPj}
                />
              )
            ) : (
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="semibold" color={labelColor}>Progresso geral</Text>
                    <Text fontSize="sm" color={subtitleColor}>{completed_missions} de {total_missions} missões concluídas</Text>
                  </Flex>
                  <Progress value={progress_pct} colorScheme={progressColorScheme(progress_pct)} size="sm" borderRadius="full" bg={progressBg} />
                </Box>

                {detail && (
                  <VStack align="stretch" spacing={3}>
                    <Box borderWidth="1px" borderColor={cardBorderColor} borderRadius="lg" p={4}>
                      <Flex align="center" gap={4} wrap="wrap">
                        <Flex
                          align="center" justify="center" flexShrink={0}
                          boxSize="56px" borderRadius="md"
                          bg={notebookIconBg}
                        >
                          <Icon as={Laptop} boxSize={6} color={notebookIconColor} />
                        </Flex>

                        <Box flex={1} minW="180px">
                          <HStack spacing={2} mb={1.5} flexWrap="wrap">
                            <Text fontSize="md" fontWeight="semibold" color={labelColor}>Notebook Onboarding</Text>
                            <Badge
                              display="inline-flex" alignItems="center" gap={1}
                              colorScheme="blue" variant="subtle" borderRadius="full" px={2}
                              textTransform="none" fontWeight="semibold"
                            >
                              <Icon as={Server} boxSize={3} /> Infraestrutura
                            </Badge>
                          </HStack>

                          {detail.freshdesk_ticket_id ? (
                            <HStack spacing={2.5} fontSize="xs" flexWrap="wrap"
                              color={ticketClosed ? "gray.400" : hintColor}
                              opacity={ticketClosed ? 0.7 : 1}
                            >
                              <HStack spacing={1.5}>
                                <Icon as={Hash} boxSize={3} />
                                <Text textDecoration={ticketClosed ? "line-through" : "none"}>
                                  Ticket #{detail.freshdesk_ticket_id}
                                </Text>
                              </HStack>
                              {ticketClosed ? (
                                <Badge colorScheme="red" variant="subtle" fontSize="xs">Fechado</Badge>
                              ) : (
                                <>
                                  <Box w="1px" h="12px" bg={cardBorderColor} />
                                  <HStack spacing={1.5}>
                                    <Icon as={Tag} boxSize={3} />
                                    <Text fontWeight="semibold" color={detail.notebook_serial ? "blue.400" : "gray.400"}>
                                      Notebook: {detail.notebook_serial ?? "Serial não atribuído"}
                                    </Text>
                                  </HStack>
                                </>
                              )}
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color="gray.400">Sem ticket aberto</Text>
                          )}
                        </Box>

                        {detail.freshdesk_ticket_id && !ticketClosed && (
                          <ChildrenInteractionBlocker
                            requiredPermissions={[PERMISSIONS.RH.CANCEL_TICKET]}
                            toastMessage="Você não tem permissão para cancelar tickets."
                          >
                            <Tooltip label="Fechar ticket no Freshdesk" fontSize="xs">
                              <Button
                                size="sm" variant="ghost" colorScheme="red"
                                isLoading={isCancelling} onClick={openConfirm}
                                flexShrink={0} alignSelf="flex-start"
                              >
                                Cancelar ticket
                              </Button>
                            </Tooltip>
                          </ChildrenInteractionBlocker>
                        )}
                      </Flex>
                    </Box>

                    <Box borderWidth="1px" borderColor={cardBorderColor} borderRadius="lg" p={4}>
                      <Flex align="center" gap={4} wrap="wrap">
                        <Flex
                          align="center" justify="center" flexShrink={0}
                          boxSize="56px" borderRadius="md"
                          bg={cyberIconBg}
                        >
                          <Icon as={Shield} boxSize={6} color={cyberIconColor} />
                        </Flex>
                        <Box flex={1} minW="180px">
                          <Text fontSize="md" fontWeight="semibold" color={labelColor} mb={2}>
                            Acesso Cyber
                          </Text>
                          {detail.cyber_login ? (
                            <VStack align="stretch" spacing={1.5}>
                              <HStack spacing={2}>
                                <Text fontSize="xs" color="gray.500" w="52px" flexShrink={0}>Login</Text>
                                <Text fontSize="xs" fontWeight="semibold" color={labelColor}>{detail.cyber_login}</Text>
                              </HStack>
                              <HStack spacing={2}>
                                <Text fontSize="xs" color="gray.500" w="52px" flexShrink={0}>Senha</Text>
                                <Text fontSize="xs" fontWeight="semibold" color={labelColor} fontFamily="mono">
                                  {showCyberPassword ? detail.cyber_password : "••••••••"}
                                </Text>
                                <IconButton
                                  aria-label={showCyberPassword ? "Ocultar senha" : "Mostrar senha"}
                                  icon={<Icon as={showCyberPassword ? EyeOff : Eye} boxSize={3.5} />}
                                  size="xs"
                                  variant="ghost"
                                  color="gray.400"
                                  _hover={{ color: cyberIconColor }}
                                  onClick={() => setShowCyberPassword(p => !p)}
                                />
                              </HStack>
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.400">Credenciais não informadas pelo Cyber</Text>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  </VStack>
                )}

                <Box>
                  <Flex align="center" justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="semibold" color={labelColor}>Missões</Text>
                    <ReminderBadge count={detail?.reminder_emails_sent ?? 0} lastSent={detail?.last_reminder_sent_at ?? null} />
                  </Flex>
                  <Text fontSize="xs" color={hintColor} mb={3}>
                    Clique em uma missão concluída para ver o que foi enviado.
                  </Text>
                  {isLoadingDetail ? (
                    <Flex justify="center" py={8}><OnflyLoading containerSize={60} /></Flex>
                  ) : (
                    missions.map((mission, idx) => (
                      <MissionListItem
                        key={mission.id} mission={mission} index={idx}
                        isLast={idx === missions.length - 1}
                        onClick={() => setSelectedMission(mission)}
                        onApprove={mission.waiting_rh ? () => handleApprove(mission.id) : undefined}
                        isApproving={approvingId === mission.id}
                        onReset={() => handleReset(mission.id)}
                        isResetting={resettingId === mission.id}
                      />
                    ))
                  )}
                  <Flex
                    align="center" gap={3} py={3} mt={1}
                    borderTopWidth="1px" borderColor={cardBorderColor}
                    cursor="pointer" borderRadius="md" px={2} mx={-2}
                    _hover={{ bg: historyRowHoverBg }}
                    onClick={() => setShowActivityLog(true)}
                    role="button"
                  >
                    <Icon as={History} color="blue.500" boxSize={5} flexShrink={0} />
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="medium" color={labelColor}>Histórico de ações</Text>
                      <Text fontSize="xs" color={hintColor}>Ver alterações</Text>
                    </Box>
                    <Icon as={ChevronRight} boxSize={4} color={hintColor} flexShrink={0} />
                  </Flex>
                </Box>
              </VStack>
            )}
            {selectedMission?.waiting_rh && (
              <WaitingRhBanner
                onApprove={() => handleApprove(selectedMission.id)}
                isApproving={approvingId === selectedMission.id}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <ConfirmCancelTicketDialog
        isOpen={isConfirmOpen} onClose={closeConfirm}
        ticketId={detail?.freshdesk_ticket_id ?? null}
        isCancelling={isCancelling} onConfirm={handleCancelTicket}
      />

      <ConfirmBenefitsEmailDialog
        isOpen={isBenefitsOpen} onClose={closeBenefits}
        candidateName={candidate?.name ?? candidate?.email ?? ""}
        isSending={isSendingBenefitsEmail} onConfirm={handleSendBenefitsEmail}
      />

      <PendencyModal
        isOpen={isPendencyOpen} onClose={closePendency}
        missions={detail?.missions ?? []}
        candidateName={candidate?.name ?? candidate?.email ?? ""}
        onSubmit={handleNotifyPendencies} isSubmitting={isNotifyingPendency}
      />
    </>
  );
};

export default CandidateDetailDrawer;
